import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import JobInfo from '../jobs/JobInfo';
import { Button } from '../common/Button';
import type { HubContext } from '../../types/hub';
import { useProfileStore } from '../../stores/profileStore';
import { useJobStore } from '../../stores/jobStore';
import { useHubStore } from '../../stores/hubStore';
import { toast } from 'react-toastify';
import GenericInformationModal from '../common/GenericInformationModal';
import EditPriorityModal from '../jobs/EditPriorityModal';
import EditJobModal from '../jobs/EditJobModal';
import PublishJobModal from '../jobs/PublishJobModal';
import { cleanupModalParamsFromUrl, decodeModalParams } from '../../utils/modalUrlParams';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { transformHubContextJobToJob } from '../../types/hub';
import {
  canRenewJob,
  getJobLifecycleState,
  isJobRenewable,
  isJobReopenable,
  isJobReviewable,
} from '../../utils/jobUtils';

const getErrorMessage = (err: unknown): string => {
  const message = (err as { message?: unknown })?.message;
  return typeof message === 'string' ? message : String(err);
};

interface JobDetailsPanelProps {
  job: HubContext['job'];
}

export const JobDetailsPanel: React.FC<JobDetailsPanelProps> = ({ job }) => {
  const { t } = useTranslation(['hub', 'jobs', 'common']);
  const { profile } = useProfileStore();

  const isOwner = profile?.id === job.client.id;

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { publishJob, cancelJob, openJob, pauseJob, renewJob, startReviewingJob } = useJobStore();
  const { fetchHubContext } = useHubStore();

  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showUpdatePriorityModal, setShowUpdatePriorityModal] = useState(false);
  const [showEditJobModal, setShowEditJobModal] = useState(false);
  const [showPublishJobConfirmation, setShowPublishJobConfirmation] = useState(false);
  const [showEditJobConfirmationModal, setShowEditJobConfirmationModal] = useState(false);
  const [showJobCancelConfirmationModal, setShowJobCancelConfirmationModal] = useState(false);

  // Shared, mutually-exclusive pending action for the four lifecycle
  // mutations below (renew/pause/reopen/start-reviewing) -- previously each
  // had its own boolean flag disabling only its own button (renew was the
  // only one that had one at all), so a different lifecycle button stayed
  // clickable while one was already in flight, letting a user fire a second
  // mutation before the first resolved. Tracking which single action is
  // pending lets every lifecycle button disable together while each still
  // shows its own "-ing" copy only when it's the one actually in flight.
  const [pendingAction, setPendingAction] = useState<
    'renew' | 'pause' | 'reopen' | 'startReview' | null
  >(null);
  const isTransitioning = pendingAction !== null;

  const closeAllModals = () => {
    setShowPublishModal(false);
  };

  const handlePublishJob = async (
    listingTypeId: number,
    expiresAt?: string,
    selectionDate?: string,
    publicationRequestId?: string
  ) => {
    if (!job) return;
    try {
      await publishJob(job.id, listingTypeId, expiresAt, selectionDate, publicationRequestId);
      setShowPublishModal(false);
      setShowUpdatePriorityModal(false);
      setShowPublishJobConfirmation(true);
    } catch (error) {
      console.error('Publish error:', error);
      const message = getErrorMessage(error);
      toast.error(
        message.includes('JOB_ALREADY_PUBLISHED')
          ? t('publish.already_published_error', { ns: 'jobs' })
          : t('publish.error', { ns: 'jobs' })
      );
    }
  };

  const openEditModal = () => {
    closeAllModals();
    setShowEditJobModal(true);
  };

  const handleCancelJob = async () => {
    if (!job) return;
    try {
      await cancelJob(job.id);
      setShowJobCancelConfirmationModal(false);
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(t('cancel_job_error'));
    }
  };

  const handleRenewJob = async () => {
    if (!job) return;
    setPendingAction('renew');
    try {
      await renewJob(job.id);
      await fetchHubContext(job.id);
      toast.success(t('renew.success', { ns: 'jobs' }));
    } catch (error) {
      console.error('Renew error:', error);
      toast.error(t('renew.error', { ns: 'jobs' }));
    } finally {
      setPendingAction(null);
    }
  };

  const isPublished =
    job.visibility === 'private' || (job.visibility === 'public' && job.published_at);
  const isJobActive = job.status === 'paused' || job.status === 'open';
  const isJobPausable = job.visibility === 'public' && job.published_at && isJobActive;
  const isJobPublishable = job.visibility === 'public' && !job.published_at && isJobActive;
  const isJobEditable = job.status === 'open' || job.status === 'paused';
  const isJobStatusReviewable =
    job.visibility === 'public' &&
    job.published_at &&
    (job.status === 'open' || job.status === 'expired');
  const canReviewJobQuotes = isJobReviewable(job);

  const handleOpenJob = async () => {
    if (!job) return;
    setPendingAction('reopen');
    try {
      await openJob(job.id);
      await fetchHubContext(job.id);
      toast.success(t('reopen_job_success', { ns: 'jobs' }));
    } catch (error) {
      console.error('Open error:', error);
      toast.error(t('reopen_job_error', { ns: 'jobs' }));
    } finally {
      setPendingAction(null);
    }
  };

  const handlePauseJob = async () => {
    if (!job) return;
    setPendingAction('pause');
    try {
      await pauseJob(job.id);
      await fetchHubContext(job.id);
      toast.success(t('pause_job_success', { ns: 'jobs' }));
    } catch (error) {
      console.error('Pause error:', error);
      toast.error(t('pause_job_error', { ns: 'jobs' }));
    } finally {
      setPendingAction(null);
    }
  };

  const handleStartReviewingJob = async () => {
    if (!job) return;
    setPendingAction('startReview');
    try {
      await startReviewingJob(job.id);
      await fetchHubContext(job.id);
      toast.success(t('start_reviewing_success', { ns: 'jobs' }));
    } catch (error) {
      console.error('Start reviewing error:', error);
      toast.error(t('start_reviewing_error', { ns: 'jobs' }));
    } finally {
      setPendingAction(null);
    }
  };

  useEffect(() => {
    const modalParams = decodeModalParams(searchParams);
    if (modalParams?.type === 'publishJob' && modalParams.jobId) {
      if (job) {
        setShowPublishModal(true);
      }
    }
  }, [job, searchParams]);

  return (
    <>
      <JobInfo job={job} />

      {job.description && (
        <div>
          <h3 className="mb-1 text-sm font-medium text-secondary">
            {t('job_description', { ns: 'hub' })}
          </h3>
          <p className="text-sm text-gray-700">{job.description}</p>
        </div>
      )}

      {isOwner && (
        <div
          className={`flex flex-col gap-4 md:items-center flex-wrap 
               ${isPublished ? 'md:flex-row-reverse' : 'flex-col-reverse md:flex-row md:justify-end '}`}
        >
          {isJobEditable && (
            <Button
              data-testid="edit-edit_job_button"
              className="w-full md:w-auto md:px-8"
              variant={isPublished ? 'primary' : 'transparent-with-border'}
              onClick={() => openEditModal()}
            >
              {t('edit_job_button', { ns: 'jobs' })}
            </Button>
          )}

          {isJobPublishable &&
            (isPublished ? (
              <Button
                onClick={() => {
                  setShowUpdatePriorityModal(true);
                }}
                className="w-full md:w-auto md:px-8"
                variant="transparent-with-border"
              >
                {t('change_priority_button', { ns: 'jobs' })}
              </Button>
            ) : (
              <Button
                onClick={() => setShowPublishModal(true)}
                className="w-full md:w-auto md:px-8"
                variant="primary"
              >
                {t('publish_job_button', { ns: 'jobs' })}
              </Button>
            ))}

          {isJobPausable &&
            (job.status === 'paused' ? (
              isJobReopenable(job) ? (
                <Button
                  data-testid="edit-edit_job_button"
                  className="w-full md:w-auto md:px-8"
                  variant="transparent-with-border"
                  onClick={handleOpenJob}
                  disabled={isTransitioning}
                >
                  {pendingAction === 'reopen'
                    ? t('reopening', { ns: 'jobs' })
                    : t('reopen_offer', { ns: 'jobs' })}
                </Button>
              ) : (
                <p className="text-sm text-gray-500">
                  {t('reopen_unavailable_notice', { ns: 'jobs' })}
                </p>
              )
            ) : (
              <Button
                className="w-full md:w-auto md:px-8"
                variant="transparent-with-border"
                onClick={handlePauseJob}
                disabled={isTransitioning}
              >
                {pendingAction === 'pause'
                  ? t('pausing', { ns: 'jobs' })
                  : t('pause_offer', { ns: 'jobs' })}
              </Button>
            ))}

          {isJobStatusReviewable &&
            (canReviewJobQuotes ? (
              <Button
                className="w-full md:w-auto md:px-8"
                variant="transparent-with-border"
                onClick={handleStartReviewingJob}
                disabled={isTransitioning}
              >
                {pendingAction === 'startReview'
                  ? t('starting_reviewing', { ns: 'jobs' })
                  : t('start_reviewing_offer', { ns: 'jobs' })}
              </Button>
            ) : (
              <p className="text-sm text-gray-500">
                {t('no_quotes_to_review_notice', { ns: 'jobs' })}
              </p>
            ))}

          {getJobLifecycleState(job) === 'removed' ? (
            <p className="text-sm text-gray-500">{t('renew.removed_notice', { ns: 'jobs' })}</p>
          ) : (
            canRenewJob(job) &&
            (isJobRenewable(job) ? (
              <Button
                className="w-full md:w-auto md:px-8"
                variant="transparent-with-border"
                onClick={handleRenewJob}
                disabled={isTransitioning}
              >
                {pendingAction === 'renew'
                  ? t('renewing', { ns: 'jobs' })
                  : t('renew.free', { ns: 'jobs' })}
              </Button>
            ) : (
              <p className="text-sm text-gray-500">
                {t('renew.start_date_unavailable_notice', { ns: 'jobs' })}
              </p>
            ))
          )}
        </div>
      )}

      {showPublishModal && job && (
        <PublishJobModal
          job={transformHubContextJobToJob(job)}
          onClose={() => {
            setShowPublishModal(false);
            cleanupModalParamsFromUrl(searchParams, setSearchParams);
            navigate(location.pathname, { replace: true });
          }}
          onEdit={() => {
            openEditModal();
            setShowPublishModal(true);
          }}
          onPublish={handlePublishJob}
        />
      )}

      {showEditJobModal && job && (
        <EditJobModal
          job={transformHubContextJobToJob(job)}
          onClose={() => setShowEditJobModal(false)}
          onJobUpdated={() => {
            setShowEditJobModal(false);
            setShowEditJobConfirmationModal(true);
            fetchHubContext(job.id);
          }}
          onCancelChange={() => {
            setShowEditJobModal(false);
            setShowJobCancelConfirmationModal(true);
          }}
        />
      )}

      {showUpdatePriorityModal && job && (
        <EditPriorityModal
          listingType={job.listing_type}
          onClose={() => setShowUpdatePriorityModal(false)}
          onUpdate={handlePublishJob}
        />
      )}

      {showPublishJobConfirmation && job && (
        <GenericInformationModal
          title={t('job_published', { ns: 'jobs' })}
          message={
            <Trans
              i18nKey="job_hint"
              ns="jobs"
              values={{ name: job.title }}
              components={{ strong: <strong /> }}
            />
          }
          onClose={() => setShowPublishJobConfirmation(false)}
          closeButtonLabel={t('back_to_job', { ns: 'jobs' })}
        />
      )}

      {showEditJobConfirmationModal && job && (
        <GenericInformationModal
          title={t('job_updated', { ns: 'jobs' })}
          message={
            <Trans
              i18nKey="job_hint"
              ns="jobs"
              values={{ name: job.title }}
              components={{ strong: <strong /> }}
            />
          }
          onClose={() => setShowEditJobConfirmationModal(false)}
          closeButtonLabel={t('back_to_job', { ns: 'jobs' })}
        />
      )}

      {showJobCancelConfirmationModal && job && (
        <GenericInformationModal
          title={t('cancel_job', { ns: 'jobs' })}
          message={
            <Trans
              i18nKey="cancel_job_hint"
              ns="jobs"
              values={{ name: job.title }}
              components={{ strong: <strong /> }}
            />
          }
          onClose={() => setShowJobCancelConfirmationModal(false)}
          onConfirm={handleCancelJob}
          closeButtonLabel={t('cancel_changes')}
          confirmButtonLabel={t('cancel_and_confirm')}
        />
      )}
    </>
  );
};

export default JobDetailsPanel;
